import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { MagnifyingGlass, CalendarBlank, Clock } from "@phosphor-icons/react";
import { formatPrice, formatDateBR } from "./format";
import { useLocale } from "@/i18n";

export default function LookupDialog({ apiBase }) {
  const { t, locale } = useLocale();
  const [open, setOpen] = useState(false);
  const [phone, setPhone] = useState("");
  const [code, setCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [booking, setBooking] = useState(null);

  const handleLookup = async (e) => {
    e.preventDefault();
    setLoading(true);
    setBooking(null);
    try {
      const res = await axios.get(`${apiBase}/lookup`, { params: { phone, code } });
      setBooking(res.data);
    } catch {
      toast.error(t("lookup_not_found"));
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = async () => {
    try {
      await axios.post(`${apiBase}/cancel`, { phone, code });
      toast.success(t("cancel_success"));
      setBooking({ ...booking, status: "cancelado" });
    } catch (err) {
      if (err.response?.status === 422) toast.warning(t("cancel_too_late"));
      else toast.error(t("cancel_error"));
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <div className="text-center pt-2">
        <DialogTrigger asChild>
          <button
            data-testid="lookup-trigger-btn"
            className="text-sm text-zinc-500 hover:text-amber-500 transition-colors underline underline-offset-4"
          >
            {t("lookup_link")}
          </button>
        </DialogTrigger>
      </div>

      <DialogContent className="bg-zinc-900 border-zinc-800 text-zinc-100 max-w-sm" data-testid="lookup-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-xl">{t("lookup_title")}</DialogTitle>
          <DialogDescription className="text-zinc-500">{t("lookup_desc")}</DialogDescription>
        </DialogHeader>

        <form onSubmit={handleLookup} className="space-y-3 mt-2" data-testid="lookup-form">
          <input
            data-testid="lookup-phone-input"
            type="tel"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            placeholder={t("lookup_phone_ph")}
            className="w-full h-11 px-4 rounded-lg bg-zinc-950 border border-zinc-800 text-zinc-100 placeholder:text-zinc-600 focus:border-amber-500 focus:ring-1 focus:ring-amber-500 focus:outline-none"
          />
          <input
            data-testid="lookup-code-input"
            type="text"
            value={code}
            onChange={(e) => setCode(e.target.value)}
            placeholder={t("lookup_code_ph")}
            className="w-full h-11 px-4 rounded-lg bg-zinc-950 border border-zinc-800 text-zinc-100 placeholder:text-zinc-600 uppercase focus:border-amber-500 focus:ring-1 focus:ring-amber-500 focus:outline-none"
          />
          <button
            data-testid="lookup-submit-btn"
            type="submit"
            disabled={loading}
            className="w-full h-11 rounded-lg bg-amber-500 text-zinc-950 font-bold flex items-center justify-center gap-2 hover:bg-amber-400 transition-colors active:scale-95 disabled:opacity-60"
          >
            <MagnifyingGlass size={16} weight="bold" />
            {loading ? t("searching") : t("search")}
          </button>
        </form>

        {booking && (
          <div className="mt-4 bg-zinc-950 border border-zinc-800 rounded-xl p-4 space-y-2 animate-fade-up" data-testid="lookup-result">
            <div className="flex items-center justify-between">
              <p className="font-bold text-zinc-100">{booking.service_name}</p>
              <span
                data-testid="lookup-status"
                className={`text-xs font-bold px-2 py-1 rounded-full ${
                  booking.status === "confirmado"
                    ? "bg-amber-500/15 text-amber-500"
                    : "bg-red-500/15 text-red-400"
                }`}
              >
                {booking.status === "confirmado" ? t("status_confirmed") : t("status_canceled")}
              </span>
            </div>
            <p className="flex items-center gap-2 text-sm text-zinc-400">
              <CalendarBlank size={15} className="text-amber-500" weight="duotone" />
              {formatDateBR(booking.date, locale)} {t("at")} {booking.time}
            </p>
            <p className="flex items-center gap-2 text-sm text-zinc-400">
              <Clock size={15} className="text-amber-500" weight="duotone" />
              {booking.duration_min} min · {formatPrice(booking.price, locale)} {t("paid_at_shop_suffix")}
            </p>
            {booking.status === "confirmado" && (
              <button
                data-testid="cancel-booking-btn"
                onClick={handleCancel}
                className="w-full mt-2 h-10 rounded-lg border border-red-500/40 text-red-400 text-sm font-semibold hover:bg-red-500/10 transition-colors"
              >
                {t("cancel_booking")}
              </button>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
